import { useMutation, useQuery } from "@apollo/client/react";
import { yupResolver } from "@hookform/resolvers/yup";
import FormInputDragFile from "@repo/components/src/forms/dragFile";
import FormInputMoney from "@repo/components/src/forms/money";
import FormInputText from "@repo/components/src/forms/text";
import FormInputTextArea from "@repo/components/src/forms/textArea";
import ModalProvider from "@repo/components/src/globalProviders/modal";
import LoadingButton from "@repo/components/src/shared/loadingButton";
import { Box } from "@repo/components/src/ui/box";
import { Heading } from "@repo/components/src/ui/heading";
import { Icon } from "@repo/components/src/ui/icon";
import {
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  ModalHeader,
} from "@repo/components/src/ui/modal";
import { Spinner } from "@repo/components/src/ui/spinner";
import { VStack } from "@repo/components/src/ui/vstack";
import {
  CurrencyCode,
  toCurrencyCode,
} from "@repo/components/src/utils/money/currencies";
import ReactNativeFile from "@repo/graphql/client/file";
import {
  EditProductDocument,
  EditProductMutation,
  EditProductMutationVariables,
} from "@repo/graphql/documents/product/mutation.generated";
import {
  GetProductSelectionDocument,
  GetProductSelectionQuery,
} from "@repo/graphql/documents/product/query.generated";
import { X } from "lucide-react-native";
import React, { useCallback } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { productInputSchema } from "./schema";
import { ProductInputSchema } from "./types";

type Props = {
  mutationOptions?: useMutation.Options<
    EditProductMutation,
    EditProductMutationVariables
  >;
  productId: string;
  currency: CurrencyCode | null;
};

/**
 * Rendered by the global ModalProvider, outside the brand layout, so the
 * caller passes the brand currency in.
 */
const EditProduct = React.memo((props: Props) => {
  const { productId, mutationOptions, currency } = props;
  const { data } = useQuery(GetProductSelectionDocument, {
    variables: { id: productId },
  });
  const originalProduct = data?.product.get;
  // The existing product's own price currency is authoritative once it
  // loads; the brand's is only a placeholder until either has resolved.
  const currencyCode =
    toCurrencyCode(originalProduct?.price?.currency.code) ?? currency;

  if (!currencyCode) {
    return (
      <>
        <ModalHeader>
          <Heading>Editar producto</Heading>
          <ModalCloseButton>
            <Icon as={X} size="lg" />
          </ModalCloseButton>
        </ModalHeader>
        <ModalBody>
          <Box className="items-center justify-center p-8">
            <Spinner />
          </Box>
        </ModalBody>
      </>
    );
  }

  return (
    <EditProductForm
      productId={productId}
      mutationOptions={mutationOptions}
      originalProduct={originalProduct}
      currencyCode={currencyCode}
    />
  );
});

EditProduct.displayName = "EditProduct";

type EditProductFormProps = {
  productId: string;
  mutationOptions?: useMutation.Options<
    EditProductMutation,
    EditProductMutationVariables
  >;
  originalProduct: GetProductSelectionQuery["product"]["get"] | undefined;
  currencyCode: CurrencyCode;
};

const EditProductForm = React.memo((props: EditProductFormProps) => {
  const { productId, mutationOptions, originalProduct, currencyCode } = props;
  const originalPrice = originalProduct?.price;
  const originalImage = originalProduct?.image;
  const [editProduct, { loading }] = useMutation(EditProductDocument, {
    ...mutationOptions,
    onCompleted: (productData) => {
      ModalProvider.hide();
      mutationOptions?.onCompleted?.(productData);
    },
  });
  const formProps = useForm<ProductInputSchema>({
    resolver: yupResolver(productInputSchema),
    defaultValues: {
      name: originalProduct?.name ?? "",
      price: {
        amount: String(originalPrice?.amount ?? "0"),
        currency: originalPrice?.currency.code ?? currencyCode,
      },
      description: originalProduct?.description ?? "",
      image:
        (!!originalImage &&
          originalImage.url !== "" &&
          new ReactNativeFile({
            uri: originalImage.url,
            name: originalImage.name,
            type: "image/jpeg",
          })) ||
        null,
    },
    mode: "onChange",
  });
  const onSubmit: SubmitHandler<ProductInputSchema> = useCallback(
    (data) => {
      editProduct({
        variables: {
          product: {
            name: data.name,
            description: data.description,
            link: "",
            price: data.price,
            image: data.image,
          },
          productId: productId,
        },
      });
    },
    [editProduct, productId],
  );
  return (
    <>
      <ModalHeader>
        <Heading>Editar producto</Heading>
        <ModalCloseButton>
          <Icon as={X} size="lg" />
        </ModalCloseButton>
      </ModalHeader>
      <ModalBody>
        <VStack space="md" className="mt-3">
          <FormInputText
            control={formProps.control}
            name={"name"}
            placeholder={"Escribe el nombre de tu producto"}
            label={"Nombre del producto"}
            required={false}
            disabled={false}
            className={""}
            size={"sm"}
            keyboardType={"default"}
            textContentType={"none"}
            autoCapitalize={"none"}
            labelSize={"sm"}
          />
          <FormInputMoney
            control={formProps.control}
            name={"price"}
            placeholder={"Escribe el precio estimado"}
            label={"Precio estimado"}
            required={false}
            disabled={false}
            className={""}
            size={"sm"}
          />
          <FormInputTextArea
            control={formProps.control}
            name={"description"}
            placeholder={"Escribe la descripción de tu producto"}
            label={"Descripción"}
            required={false}
            disabled={false}
            className={""}
            size={"sm"}
            keyboardType={"default"}
            textContentType={"none"}
            autoCapitalize={"none"}
            labelSize={"sm"}
          />
          <FormInputDragFile
            control={formProps.control}
            name="image"
            labelSize="sm"
            iconSize="xl"
            helperText="Arrastra una foto de tu producto o búscalas acá"
            size="sm"
            multiple={false}
          />
        </VStack>
      </ModalBody>
      <ModalFooter>
        <LoadingButton
          action="brand"
          size="sm"
          rounded="lg"
          onPress={() => formProps.handleSubmit(onSubmit)()}
          loading={loading}
          loadingText="Guardando..."
        >
          Guardar
        </LoadingButton>
      </ModalFooter>
    </>
  );
});

EditProductForm.displayName = "EditProductForm";

export default EditProduct;
